import React, { useCallback, useMemo, useRef, useEffect, memo } from "react";
import { uniqueId } from "lodash";
import { useTranslation } from "react-i18next";

import { useTypedDispatch, useTypedSelector } from "../../../redux/store";
import openOrderCard from "../../../redux/services/Order/card/openOrderCard";
import orderPage from "../../../redux/reducers/OrdersPage";
import {
	ORDERS_TABLE_PAGE_SIZE,
	ORDERS_TYPES,
} from "../../../redux/constants/OrdersPage";
import {
	selectActiveOrderId,
	selectCurrentTabOrders,
} from "../../../redux/reducers/OrdersPage/selectors";
import {
	useColumns,
	useSort,
	useVisibleColumns,
	useWidths,
} from "../../../hooks/useTableSettings";
import { useCheckFlagAccessControl } from "../../../hooks";
import { applyOrderFilters } from "../../common/Modal/ArchiveOrderFiltersModal";

import { ColumnId } from "./components/ModelTable/columns";
import { filterOrdersByPageFilters } from "./filterOrdersByPageFilters";
import ModelTable from "./components/ModelTable";
import { TabAccessNames, TabKeys } from "./constants/access";

export const orderTableByType = {
	live: "orderLive",
	closed: "orderClosed",
	preliminary: "orderPreliminary",
	executable: "orderExecutable",
	all: "orderAll",
} as const;

type OrderTableTypeKeys = keyof typeof orderTableByType;

export type OrderTableTypeValues =
	(typeof orderTableByType)[OrderTableTypeKeys];

const OrderTable: React.FC = () => {
	const { t } = useTranslation();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);
	const dispatch = useTypedDispatch();

	const settingsLanguage = useTypedSelector(
		(state) => state.session.language,
	);

	const ordersType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const hideOrderIds = useTypedSelector(
		(state) => state.ordersPageReducer.hideOrderIds,
	);
	const orders = useTypedSelector(selectCurrentTabOrders);
	const activeOrderId = useTypedSelector(selectActiveOrderId);
	const activeOrderPointsLength = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder?.points?.length || 0,
	);
	const activeOrderRouteLength = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder?.route?.length || 0,
	);
	const filters = useTypedSelector(
		(state) => state.ordersPageReducer.filters[ordersType],
	);
	const valueModalMultyFiltersTabClosed = useTypedSelector(
		(state) =>
			state.ordersPageReducer.filters[ORDERS_TYPES.CLOSED].byMultyFilters,
	);

	const isClosedTab = useMemo(
		() => ordersType === ORDERS_TYPES.CLOSED,
		[ordersType],
	);
	const isNotClosedTab = useMemo(() => !isClosedTab, [isClosedTab]);

	const tableId = useMemo(
		() => `main-order-page-${ordersType}1`,
		[ordersType],
	);
	const tableType = useMemo(() => orderTableByType[ordersType], [ordersType]);

	const columns = useColumns(tableType);
	const { columnIds, setColumnIds } = useVisibleColumns(tableId, tableType);
	const { sortMethod, setSortMethod } = useSort(tableId, tableType);
	const { widths, setWidths } = useWidths(tableId, tableType);

	const modelTableColumns = useMemo<ModelTable.Column[]>(
		() =>
			columns
				.map((column) => ({
					id: column.id as ColumnId,
					width: widths[column.id],
					visible: columnIds.includes(column.id as ColumnId),
				}))
				.sort((leftColumn, rightColumn) => {
					const leftIndex = columnIds.indexOf(leftColumn.id);
					const rightIndex = columnIds.indexOf(rightColumn.id);

					return leftIndex - rightIndex;
				}),
		[columnIds, columns, widths],
	);

	const context = useRef<any>({});

	context.current.activeOrderId = activeOrderId;

	const selectOrder = useCallback(
		(orderId: number) => {
			if (context.current.activeOrderId === orderId) return;

			const original =
				orders.items.find(
					(item) =>
						item.id === orderId && !hideOrderIds.includes(orderId),
				) ||
				orders.items.find(
					(item) =>
						!hideOrderIds.includes(orderId) &&
						!hideOrderIds.includes(item.id),
				);

			if (original) {
				dispatch(
					orderPage.actions.setActiveOrder({
						...original,
						hash: uniqueId("order"),
					}),
				);

				if (original.points.length === 1) {
					dispatch(orderPage.actions.setActivePoint(0));
				}
			}
		},
		[orders.items, hideOrderIds, dispatch],
	);

	const editOrder = useCallback(
		(orderId: number) => {
			dispatch(openOrderCard(orderId));
		},
		[dispatch],
	);

	const modelTableOnChangeColumns = useCallback(
		(columns: ModelTable.Column[]) => {
			setColumnIds(
				columns
					.filter((column) => column.visible)
					.map((column) => column.id),
			);

			setWidths(
				columns.reduce((accumulator, column) => {
					accumulator[column.id] = column.width || 100;

					return accumulator;
				}, {} as Record<ColumnId, number>),
			);
		},
		[setColumnIds, setWidths],
	);

	const modelTableOnLoadMore = useCallback(() => {
		if (!isClosedTab) return;
		dispatch(
			orderPage.actions.setFilters({
				type: ordersType,
				data: {
					...filters,
					limit:
						(filters.limit || ORDERS_TABLE_PAGE_SIZE) +
						ORDERS_TABLE_PAGE_SIZE,
				},
			}),
		);
	}, [dispatch, filters, isClosedTab, ordersType]);

	const items = useMemo(() => {
		const models = orders?.items;

		if (!Array.isArray(models) || models.length === 0) return [];

		if (isNotClosedTab) {
			return filterOrdersByPageFilters(
				models,
				filters,
				t(["point_on_map"]),
			);
		}

		if (isClosedTab) {
			return applyOrderFilters(
				models,
				valueModalMultyFiltersTabClosed,
				t,
			);
		}

		return models;
	}, [
		filters,
		isClosedTab,
		isNotClosedTab,
		orders?.items,
		t,
		valueModalMultyFiltersTabClosed,
	]);

	// Паддинг пустыми строками нужен только архиву: там серверная
	// пагинация, и строки ниже загруженных ещё не пришли. Считаем его от
	// незагруженных строк, а не от общего total: иначе после клиентского
	// фильтра таблица дорисовывала пустые строки вместо отсеянных заказов.
	// В живых вкладках весь список уже на клиенте — паддинг не нужен.
	const tableTotalCount = useMemo(() => {
		if (!isClosedTab) return undefined;
		const total = orders?.pagination?.count ?? orders?.count ?? 0;
		const loaded = orders?.items?.length ?? 0;
		return items.length + Math.max(0, total - loaded);
	}, [
		isClosedTab,
		items.length,
		orders?.count,
		orders?.items?.length,
		orders?.pagination?.count,
	]);

	const isDoubleBecauseOfAccess = useMemo(() => {
		const isAccessViewInModalOrder =
			checkFlagAccessControl[TabKeys.EDIT_ORDER];

		return isAccessViewInModalOrder;
	}, [checkFlagAccessControl]);

	useEffect(() => {
		/**
		 * Manages the visibility and selection of the active order in the order information widget.
		 *
		 * - If no orders are available (due to filters or lack of data), resets the active order to the default state.
		 * - If an active order exists but lacks route or point details, automatically selects the first available order.
		 * - Ensures that the widget does not remain empty when filters exclude all orders.
		 */
		if (items.length === 0) {
			dispatch(orderPage.actions.setActiveOrderBeDefault());
		} else if (
			items?.length &&
			!activeOrderPointsLength &&
			!activeOrderRouteLength
		) {
			selectOrder(items[0].id);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items.length]);

	const onEdit = useCallback(
		(orderId: number) => {
			if (isDoubleBecauseOfAccess) editOrder(orderId);
		},
		[editOrder, isDoubleBecauseOfAccess],
	);

	return (
		<ModelTable
			selected={activeOrderId}
			sort={sortMethod}
			columns={modelTableColumns}
			data={items}
			language={settingsLanguage}
			onChangeSelected={selectOrder}
			onChangeSort={setSortMethod}
			onChangeColumns={modelTableOnChangeColumns}
			onEdit={onEdit}
			onLoadMore={modelTableOnLoadMore}
			totalCount={tableTotalCount}
		/>
	);
};

export default memo(OrderTable);
